import {
  useAppConfigContext,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import {
  CustomIcons,
  Grid,
  Stack,
  styled,
  Text,
} from '@ltvco/refresh-lib/theme';
import { useReportMonitors, useSession } from '@ltvco/refresh-lib/v1';
import {
  IdentityCard,
  FootprintCard,
  useFeatureBlockedModal,
  FeatureBlockedModal,
} from '@ltvco/refresh-lib/v2';
import { DirectionsCar, Email, Home, PhoneIphone } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ManageReportMonitorsModal } from 'components/idMonitor/ClaimedReportMonitorsContainer/ManageReportMonitorsModal';

import { filterAndSortReports } from '../IdentityHub/utils';

import DigitalFootprintBanner from './DigitalFootprintBanner';

const StyledStack = styled(Stack)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

export const TopSection = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  width: '100%',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
  },
}));

export const TopSubtitle = styled(Text)(({ theme }) => ({
  textAlign: 'center',
  width: '75%',
  marginBottom: '24px',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    textAlign: 'left',
  },
}));

export const DigitalFootprint = () => {
  const { currentMonitors } = useReportMonitors();
  const { trackEventGA4 } = useAppConfigContext();
  const redirect = useNavigate();
  const {
    session: { account },
  } = useSession();
  const { isBlocked } = useSubscriptionAccessConfigContext();
  const {
    openFeatureBlockedModal,
    closeFeatureBlockedModal,
    isFeatureBlockedModalOpen,
  } = useFeatureBlockedModal();

  const { first_name, last_name } = account?.account?.user_info || {};

  const filteredAndSortedReports = useMemo(
    () => filterAndSortReports(currentMonitors || []),
    [currentMonitors]
  );

  const [searchType, setSearchType] = useState('');
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);

  const claimedPersonReports =
    filteredAndSortedReports['detailed_person_report'];
  const claimedPropertyReports = filteredAndSortedReports['property_report'];
  const claimedVehicleReports = filteredAndSortedReports['vehicle_report'];
  const claimedEmailReports = filteredAndSortedReports['social_network_report'];
  const claimedPhoneReports = filteredAndSortedReports['reverse_phone_report'];

  const REPORTVARIATION = {
    phone: {
      title: 'Phone',
      description: 'Get alerts if your number is exposed.',
      icon: <PhoneIphone sx={{ color: '#0052FF' }} fontSize="small" />,
      type: 'phone',
      claimedReports: claimedPhoneReports,
    },
    email: {
      title: 'Email',
      description: 'Know instantly if your email is breached.',
      icon: <Email sx={{ color: '#0052FF' }} fontSize="small" />,
      type: 'email',
      claimedReports: claimedEmailReports,
    },
    vehicle: {
      title: 'Vehicle',
      description: 'Track changes tied to your address.',
      icon: <DirectionsCar sx={{ color: '#0052FF' }} fontSize="small" />,
      type: 'vehicle',
      claimedReports: claimedVehicleReports,
    },
    property: {
      title: 'Home',
      description: 'Monitor records connected to your car.',
      icon: <Home sx={{ color: '#0052FF' }} fontSize="small" />,
      type: 'property',
      claimedReports: claimedPropertyReports,
    },
    darkweb: {
      title: 'Dark Web',
      description: 'Know if your email has been compromised.',
      icon: (
        <CustomIcons.DarkWebIcon
          color="primary"
          fontSize="small"
          sx={{ ml: 0.5 }}
        />
      ),
      onClick: () => {
        if (isBlocked('dark_web')) {
          setSearchType('Dark Web');
          openFeatureBlockedModal();
          return;
        }

        trackEventGA4({
          event_name: 'navigation_click',
          type: 'Dark Web',
          interacted_from: 'digital_footprint',
        });
        redirect('/dashboard/dark-web');
      },
      isClaimed: true,
    },
  };

  const claimProgress =
    (claimedPersonReports?.length ?? 0) +
    (claimedPhoneReports?.length ?? 0) +
    (claimedEmailReports?.length ?? 0) +
    (claimedVehicleReports?.length ?? 0) +
    (claimedPropertyReports?.length ?? 0);

  return (
    <>
      <Stack direction="column" padding={{ xs: 2.5, sm: 0 }}>
        <Stack spacing={2} mt={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CustomIcons.DigitalFootprintIcon sx={{ fontSize: '1.25rem' }} />
            <Text variant="h3">Digital Footprint</Text>
          </Stack>
        </Stack>

        {claimProgress !== 5 && <DigitalFootprintBanner />}

        <StyledStack
          width="100%"
          gap={3}
          direction="column"
          marginTop={2}
          marginBottom={3}
        >
          <IdentityCard
            onFootprintPage
            firstName={first_name || ''}
            lastName={last_name || ''}
            claimedPersonReports={claimedPersonReports}
          />
          <Grid container spacing={3} width="100%" flexWrap="wrap">
            {Object.values(REPORTVARIATION).map((item: any) => (
              <Grid size={{ xs: 12, sm: 6 }} key={item?.type}>
                <FootprintCard
                  onFootprintPage
                  title={item?.title}
                  icon={item?.icon}
                  type={item?.type}
                  claimedReports={item?.claimedReports}
                  Modal={ManageReportMonitorsModal}
                  onClick={item?.onClick}
                  isClaimedReport={item?.isClaimed}
                />
              </Grid>
            ))}
          </Grid>
        </StyledStack>
      </Stack>

      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={closeFeatureBlockedModal}
        searchType={searchType}
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
      />
    </>
  );
};
